import React, { useState } from 'react';
import { ArrowLeft, Heart, MapPin, Clock, DollarSign, Navigation, Share2, CheckCircle, AlertCircle } from 'lucide-react';
import { DatePlan } from '../types';

interface ItineraryProps {
  plan: DatePlan;
  onBack: () => void;
  onSave: (plan: DatePlan) => void;
  onComplete: () => void;
}

export default function Itinerary({ plan, onBack, onSave, onComplete }: ItineraryProps) {
  const [activeTab, setActiveTab] = useState<'timeline' | 'map' | 'budget'>('timeline');
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const costPerPerson = Math.round(plan.estimatedCost / 2);

  const handleOpenGoogleMaps = (placeName: string) => {
    const url = `https://www.google.com/maps/search/${encodeURIComponent(placeName)}+Kolkata`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header with Backdrop */}
      <div className="bg-gradient-to-br from-rose-600 to-rose-700 text-white">
        <div className="px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="p-2 hover:bg-rose-700 rounded-lg">
            <ArrowLeft size={24} />
          </button>
          <div className="text-right">
            <div className="text-2xl font-bold">{plan.title}</div>
            <div className="text-rose-100 text-sm">{plan.subtitle}</div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="px-4 pb-4 grid grid-cols-3 gap-3">
          <div className="bg-rose-500 bg-opacity-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">{Math.round(plan.totalDuration / 60)}h</div>
            <div className="text-xs">Duration</div>
          </div>
          <div className="bg-rose-500 bg-opacity-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">₹{costPerPerson}</div>
            <div className="text-xs">Per Person</div>
          </div>
          <div className="bg-rose-500 bg-opacity-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold">{plan.noveltyScore}</div>
            <div className="text-xs">Novelty</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-0 bg-white border-b border-slate-200 flex gap-0 px-4">
        <button
          onClick={() => setActiveTab('timeline')}
          className={`flex-1 py-4 font-semibold text-center border-b-2 transition-colors ${
            activeTab === 'timeline'
              ? 'text-rose-600 border-rose-600'
              : 'text-slate-600 border-transparent hover:text-slate-900'
          }`}
        >
          Timeline
        </button>
        <button
          onClick={() => setActiveTab('map')}
          className={`flex-1 py-4 font-semibold text-center border-b-2 transition-colors ${
            activeTab === 'map'
              ? 'text-rose-600 border-rose-600'
              : 'text-slate-600 border-transparent hover:text-slate-900'
          }`}
        >
          Map
        </button>
        <button
          onClick={() => setActiveTab('budget')}
          className={`flex-1 py-4 font-semibold text-center border-b-2 transition-colors ${
            activeTab === 'budget'
              ? 'text-rose-600 border-rose-600'
              : 'text-slate-600 border-transparent hover:text-slate-900'
          }`}
        >
          Budget
        </button>
      </div>

      {/* Content */}
      <div className="px-4 pb-32">
        {/* Timeline */}
        {activeTab === 'timeline' && (
          <div className="space-y-4 pt-4">
            {plan.stops.map((stop, idx) => (
              <div key={stop.id}>
                {/* Stop Card */}
                <div className="card">
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-rose-600 text-white flex items-center justify-center font-bold">
                        {idx + 1}
                      </div>
                      {idx < plan.stops.length - 1 && (
                        <div className="w-0.5 h-12 bg-rose-200 my-2"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="text-sm font-semibold text-slate-500">{stop.startTime}</div>
                          <h3 className="text-lg font-bold text-slate-900">{stop.place.name}</h3>
                        </div>
                        <span className="badge text-xs">{stop.place.category}</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-3">{stop.place.description}</p>

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div className="flex items-center gap-1 text-slate-600">
                          <Clock size={14} />
                          {stop.duration} mins
                        </div>
                        <div className="flex items-center gap-1 text-slate-600">
                          <DollarSign size={14} />
                          ₹{stop.place.estimatedSpend}
                        </div>
                        <div className="flex items-center gap-1 text-slate-600">
                          {stop.place.indoorOutdoor === 'indoor' ? '🏠' : '🏞️'}
                          {stop.place.indoorOutdoor === 'both' ? 'Both' : stop.place.indoorOutdoor}
                        </div>
                        <div className="flex items-center gap-1 text-slate-600">
                          🚪 {stop.place.openingHours.open} - {stop.place.openingHours.close}
                        </div>
                      </div>

                      {/* CTA */}
                      <button
                        onClick={() => handleOpenGoogleMaps(stop.place.name)}
                        className="w-full py-2 bg-slate-100 text-slate-900 rounded-lg hover:bg-slate-200 text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <MapPin size={14} />
                        Open in Maps
                      </button>
                    </div>
                  </div>
                </div>

                {/* Transport Leg */}
                {stop.transportToNext && (
                  <div className="ml-5 py-3 border-l-2 border-rose-200 pl-4 flex items-center gap-3">
                    <div className="text-2xl">
                      {stop.transportToNext.mode === 'metro' && '🚇'}
                      {stop.transportToNext.mode === 'local_train' && '🚆'}
                      {stop.transportToNext.mode === 'bus' && '🚌'}
                      {stop.transportToNext.mode === 'walking' && '🚶'}
                      {stop.transportToNext.mode === 'ferry' && '⛴️'}
                      {stop.transportToNext.mode === 'auto' && '🛺'}
                      {stop.transportToNext.mode === 'cab' && '🚕'}
                    </div>
                    <div className="flex-1 text-xs">
                      <div className="font-semibold text-slate-900">
                        {stop.transportToNext.duration} min • ₹{stop.transportToNext.cost}
                      </div>
                      <div className="text-slate-600">
                        {stop.transportToNext.from} → {stop.transportToNext.to}
                      </div>
                      {!stop.transportToNext.easyForCouples && (
                        <div className="flex items-center gap-1 text-amber-600 mt-1">
                          <AlertCircle size={12} />
                          <span>May require navigation</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Map */}
        {activeTab === 'map' && (
          <div className="pt-4 space-y-4">
            <div className="card">
              <div className="w-full h-96 bg-slate-200 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <div className="text-4xl mb-2">🗺️</div>
                  <p className="text-slate-600 text-sm">
                    Interactive map would load here
                  </p>
                  <p className="text-slate-500 text-xs mt-2">
                    In production, integrate Google Maps or MapLibre
                  </p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {plan.stops.map((stop, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleOpenGoogleMaps(stop.place.name)}
                    className="py-2 px-3 bg-rose-50 text-rose-600 rounded-lg hover:bg-rose-100 text-xs font-medium text-center"
                  >
                    📍 {idx + 1}. {stop.place.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Transport Info */}
            <div className="card bg-blue-50 border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-3">🚆 Transport Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-700">Public Transport</span>
                  <span className="font-bold text-blue-900">{plan.publicTransportPercentage}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-700">Total Walking</span>
                  <span className="font-bold text-blue-900">{plan.totalWalking.toFixed(1)} km</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Budget */}
        {activeTab === 'budget' && (
          <div className="pt-4 space-y-4">
            <div className="card bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200">
              <h3 className="font-bold text-slate-900 mb-4 text-lg">Budget Breakdown</h3>

              <div className="space-y-3">
                {plan.stops.map((stop, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <div>
                      <div className="font-semibold text-slate-900">{stop.place.name}</div>
                      <div className="text-xs text-slate-600">
                        Entry: ₹{stop.place.entryFee} + Food: ₹{stop.place.estimatedSpend}
                      </div>
                    </div>
                    <div className="font-bold text-rose-600">
                      ₹{stop.place.entryFee + stop.place.estimatedSpend}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-rose-200 mt-4 pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-slate-900">Total for 2 people</span>
                  <span className="text-2xl font-bold text-rose-600">₹{plan.estimatedCost}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-slate-600">
                  <span>Per person</span>
                  <span className="font-semibold">₹{costPerPerson}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-slate-600 mt-2">
                  <span>Transport (estimated)</span>
                  <span className="font-semibold">₹200-300</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <span className="font-semibold">💡 Tip:</span> Budget includes entry fees and food. Add ~₹150-200 for transport.
              </div>
            </div>

            {/* Cost Comparison */}
            <div className="card">
              <h3 className="font-bold text-slate-900 mb-3">Budget Level</h3>
              <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-red-500"
                  style={{ width: `${Math.min((costPerPerson / 2000) * 100, 100)}%` }}
                ></div>
              </div>
              <div className="mt-2 flex justify-between text-xs text-slate-600">
                <span>Budget</span>
                <span>Premium</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="fixed bottom-20 left-0 right-0 px-4 bg-gradient-to-t from-white to-transparent pt-4">
        <div className="space-y-3">
          <button
            onClick={() => setShowCompleteModal(true)}
            className="w-full btn-primary py-4 font-bold flex items-center justify-center gap-2"
          >
            <CheckCircle size={20} />
            Mark as Done
          </button>
          <div className="flex gap-3">
            <button
              onClick={() => onSave(plan)}
              className="flex-1 btn-secondary py-3 flex items-center justify-center gap-2"
            >
              <Heart size={18} />
              Save
            </button>
            <button
              className="flex-1 btn-secondary py-3 flex items-center justify-center gap-2"
            >
              <Share2 size={18} />
              Share
            </button>
          </div>
        </div>
      </div>

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
          <div className="w-full bg-white rounded-t-3xl p-6 space-y-4">
            <h2 className="text-2xl font-bold text-slate-900">How was your date?</h2>
            <p className="text-slate-600">Your feedback helps us create better dates!</p>

            <div className="grid grid-cols-2 gap-3">
              <button className="py-3 px-4 bg-slate-100 rounded-lg hover:bg-slate-200 font-semibold text-slate-900">
                ❤️ Loved it
              </button>
              <button className="py-3 px-4 bg-slate-100 rounded-lg hover:bg-slate-200 font-semibold text-slate-900">
                🙂 Good
              </button>
              <button className="py-3 px-4 bg-slate-100 rounded-lg hover:bg-slate-200 font-semibold text-slate-900">
                😐 Average
              </button>
              <button className="py-3 px-4 bg-slate-100 rounded-lg hover:bg-slate-200 font-semibold text-slate-900">
                🙃 Not us
              </button>
            </div>

            <button
              onClick={() => {
                setShowCompleteModal(false);
                onComplete();
              }}
              className="w-full btn-primary py-3"
            >
              Add to History
            </button>
            <button
              onClick={() => setShowCompleteModal(false)}
              className="w-full btn-ghost"
            >
              Not now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
